import { NavLink, useLocation } from "react-router-dom";
import {
  ArrowLeftRight,
  CreditCard,
  FileInput,
  LayoutDashboard,
  LogOut,
  Settings,
  BarChart2,
  Table2,
  Users,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { OrgSwitcher } from "../../features/org-switcher/OrgSwitcher";
import { useLogout } from "../../hooks/useLogout";

export function Sidebar() {
  const location = useLocation();
  const isFlowActive =
    location.pathname === "/flow" ||
    location.pathname.startsWith("/flow/");
  const { logout, isLoggingOut } = useLogout();

  return (
    <aside className="shrink-0 border-b border-gray-200 bg-white lg:flex lg:h-screen lg:w-60 lg:flex-col lg:border-b-0 lg:border-r">
      <div className="flex h-14 items-center justify-between gap-2 px-4">
        <span className="shrink-0 text-lg font-semibold">Grid2</span>
        <OrgSwitcher />
      </div>
      <nav className="flex gap-1 overflow-x-auto px-2 pb-3 lg:block lg:flex-1 lg:space-y-1 lg:overflow-visible lg:py-4" aria-label="Main navigation">
        {/* Dashboard */}
        <NavLink
          to="/dashboard"
          end
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </NavLink>

        {/* Flow with sub-items */}
        <div>
          <NavLink
            to="/flow"
            end
            className={({ isActive }) =>
              cn(
                "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
              )
            }
          >
            <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            Flow
          </NavLink>

          {/* Sub-items — shown when any /flow route is active */}
          {isFlowActive && (
            <div className="ml-2 mt-0.5 flex gap-1 lg:ml-7 lg:block lg:space-y-0.5">
              <NavLink
                to="/flow"
                end
                className={({ isActive }) =>
                  cn(
                    "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    isActive
                      ? "text-gray-900 bg-gray-100"
                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-50",
                  )
                }
              >
                Pipelines
              </NavLink>
              <NavLink
                to="/flow/dashboard"
                className={({ isActive }) =>
                  cn(
                    "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    isActive
                      ? "text-gray-900 bg-gray-100"
                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-50",
                  )
                }
              >
                <BarChart2 className="h-3 w-3" aria-hidden="true" />
                Dashboard
              </NavLink>
            </div>
          )}
        </div>

        {/* Forms */}
        <NavLink
          to="/forms"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <FileInput className="h-4 w-4" aria-hidden="true" />
          Forms
        </NavLink>

        {/* Action Tables */}
        <NavLink
          to="/action-tables"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <Table2 className="h-4 w-4" aria-hidden="true" />
          Action Tables
        </NavLink>

        {/* Teams */}
        <NavLink
          to="/teams"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <Users className="h-4 w-4" aria-hidden="true" />
          Teams
        </NavLink>

        {/* Billing */}
        <NavLink
          to="/billing"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <CreditCard className="h-4 w-4" aria-hidden="true" />
          Billing
        </NavLink>

        {/* Settings */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
            )
          }
        >
          <Settings className="h-4 w-4" aria-hidden="true" />
          Settings
        </NavLink>
      </nav>

      {/* Account section — pinned to the bottom on the desktop column
          layout via `mt-auto`; on the mobile/tablet row layout it simply
          follows the nav in normal flow, so logout stays reachable there
          too instead of being clipped or hidden. */}
      <div className="border-t border-gray-100 p-2 lg:mt-auto">
        <button
          type="button"
          onClick={() => void logout()}
          disabled={isLoggingOut}
          aria-label={isLoggingOut ? "Signing out" : "Log out"}
          className="flex w-full items-center gap-3 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60"
        >
          <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
          {isLoggingOut ? "Signing out…" : "Log out"}
        </button>
      </div>
    </aside>
  );
}
